import ContentCopy from '@mui/icons-material/ContentCopy';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import { keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AiToolCallState, LLMMessageRole } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import {
  lazy,
  ReactNode,
  Suspense,
  UIEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useStickToBottom } from 'use-stick-to-bottom';

import { CopyToClipboard } from '~components/CopyToClipboard/CopyToClipboard';
import { TreadAgentMessage, TreadAgentVisualization } from '~hooks/useTreadAgent/models';
import useTreadAgent from '~hooks/useTreadAgent/useTreadAgent';
import { useStores } from '~store';
import BulkConfirmationCard from '~tread-agent/BulkConfirmationCard';
import ConfirmationCard from '~tread-agent/ConfirmationCard';
import MessageAttachments from '~tread-agent/MessageAttachments';
import ErrorPart from '~tread-agent/parts/ErrorPart';
import TextPart from '~tread-agent/parts/TextPart';
import TurnTraceDisclosure from '~tread-agent/trace/TurnTraceDisclosure';
import TurnTraceLive from '~tread-agent/trace/TurnTraceLive';
import TreadAgentLoader from '~tread-agent/TreadAgentLoader';
import { BulkConfirmDecision, CompletedTurnTrace } from '~tread-agent/types';
import { useConversationView } from '~tread-agent/useConversationView';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

// Loaded on first chart so the charting library stays out of the app's main bundle.
const VisualizationPart = lazy(() => import('~tread-agent/parts/VisualizationPart'));

// Matches the height a chart takes once loaded, so the transcript doesn't jump.
const VISUALIZATION_FALLBACK_HEIGHT_IN_PX = 240;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Applied to settled messages so they ease in as they appear. Disabled under reduced-motion.
const enterAnimationSx = {
  animation: `${fadeInUp} 0.2s ease`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

// Absolute timestamp revealed on message hover. tz is applied at display time rather than at storage time.
function formatMessageTimestamp(createdAt: Dayjs) {
  return dayjs.tz(createdAt).format('MMM D, h:mm A');
}

// Compact stopwatch label shown on the live trace line: "4.2s" (one decimal) under a minute,
// "M:SS" beyond.
function formatElapsed(elapsedMs: number) {
  const totalSeconds = elapsedMs / 1000;

  if (totalSeconds < 60) {
    return `${totalSeconds.toFixed(1)}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function UserMessageBubble({ message }: { message: TreadAgentMessage }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        alignItems: 'flex-end',
        alignSelf: 'flex-end',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        maxWidth: '85%',
        '&:hover .msg-timestamp': { opacity: 1 },
        ...enterAnimationSx,
      }}
    >
      {message.files.length > 0 && <MessageAttachments files={message.files} />}

      {/* A file-only message has no text bubble. */}
      {message.content && (
        <Box
          sx={{
            bgcolor: theme.brandV2.colors.treadGray8,
            borderRadius: theme.brandV2.borderRadius,
            px: 1.5,
            py: 1,
          }}
        >
          <Typography
            sx={{ fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {message.content}
          </Typography>
        </Box>
      )}

      <Typography
        className="msg-timestamp"
        sx={{
          color: theme.brandV2.colors.treadGray3,
          fontSize: '11px',
          opacity: 0,
          transition: 'opacity 0.15s ease',
        }}
      >
        {formatMessageTimestamp(message.createdAt)}
      </Typography>
    </Box>
  );
}

type AssistantTurn =
  | {
      kind: 'persisted';
      message: TreadAgentMessage;
      turnTrace: CompletedTurnTrace | null;
    }
  | {
      kind: 'live';
      error: ReactNode;
      streaming: boolean;
      text: string;
      trace: ReactNode;
    };

function ToolCallCards({
  busyToolCallIds,
  onBulkConfirmToolCalls,
  onCancelToolCall,
  onConfirmToolCall,
  toolCalls,
}: {
  busyToolCallIds: string[];
  onBulkConfirmToolCalls: (decision: BulkConfirmDecision) => void;
  onCancelToolCall: (toolCallId: string) => void;
  onConfirmToolCall: (toolCallId: string) => void;
  toolCalls: TreadAgentMessage['toolCalls'];
}) {
  if (toolCalls.length > 1) {
    return (
      <BulkConfirmationCard
        isBusy={toolCalls.some(
          (toolCall) =>
            toolCall.state === AiToolCallState.CONFIRMATION_REQUIRED &&
            busyToolCallIds.includes(toolCall.id),
        )}
        onSubmit={onBulkConfirmToolCalls}
        toolCalls={toolCalls}
      />
    );
  }

  return (
    <>
      {toolCalls.map((toolCall) => (
        <ConfirmationCard
          key={toolCall.id}
          isBusy={busyToolCallIds.includes(toolCall.id)}
          onCancel={() => onCancelToolCall(toolCall.id)}
          onConfirm={() => onConfirmToolCall(toolCall.id)}
          output={toolCall.output}
          preview={toolCall.preview}
          state={toolCall.state}
          toolName={toolCall.name}
        />
      ))}
    </>
  );
}

// A chart renders outside the assistant message block. A chart turn is saved as a chart-only
// message plus the text answer, so inside the block the chart would remount when the live turn
// hands off to them; keyed by its own id it keeps its place.
function VisualizationBlock({
  visualization,
}: {
  visualization: TreadAgentVisualization;
}) {
  return (
    <Box sx={{ alignSelf: 'flex-start', maxWidth: '95%', width: '100%' }}>
      <Suspense
        fallback={
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              height: VISUALIZATION_FALLBACK_HEIGHT_IN_PX,
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        }
      >
        <VisualizationPart visualization={visualization} />
      </Suspense>
    </Box>
  );
}

// An assistant turn: the trace of the turn, its text, then a confirmation card for each write
// action it invoked (oldest-first). Read/lookup tool calls are not surfaced by the messages read,
// so only write-action cards appear here — the read steps live in the trace instead. Also renders
// the live turn, so the handoff to its persisted message updates in place.
function AssistantMessageBlock({
  busyToolCallIds,
  justStreamed,
  onBulkConfirmToolCalls,
  onCancelToolCall,
  onConfirmToolCall,
  turn,
}: {
  busyToolCallIds: string[];
  // True for the message this turn just streamed live: it was already on screen, so skip the
  // enter animation when it re-mounts as the persisted row (otherwise it flashes/re-slides at
  // the end of every turn). History messages loaded on scroll still fade in.
  justStreamed: boolean;
  onBulkConfirmToolCalls: (decision: BulkConfirmDecision) => void;
  onCancelToolCall: (toolCallId: string) => void;
  onConfirmToolCall: (toolCallId: string) => void;
  turn: AssistantTurn;
}) {
  const theme = useTheme();
  const narration = turn.kind === 'live' ? turn.text : turn.message.content;
  const trace =
    turn.kind === 'live'
      ? turn.trace
      : turn.turnTrace && (
          <TurnTraceDisclosure
            elapsedSeconds={turn.turnTrace.elapsedSeconds}
            trace={turn.turnTrace.trace}
          />
        );

  return (
    <Box
      data-test-id="tread-agent-assistant-message"
      sx={{
        alignSelf: 'flex-start',
        maxWidth: '95%',
        width: '100%',
        '&:hover .assistant-meta': { opacity: 1 },
        ...(justStreamed || turn.kind === 'live' ? {} : enterAnimationSx),
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {trace}

        {/* A silent-write turn carries only a confirmation card, no narration, so no empty
            text bubble renders. */}
        {narration && (
          <Box aria-live="polite">
            <TextPart
              streaming={turn.kind === 'live' && turn.streaming}
              text={narration}
            />
          </Box>
        )}

        {turn.kind === 'persisted' && (
          <ToolCallCards
            busyToolCallIds={busyToolCallIds}
            onBulkConfirmToolCalls={onBulkConfirmToolCalls}
            onCancelToolCall={onCancelToolCall}
            onConfirmToolCall={onConfirmToolCall}
            toolCalls={turn.message.toolCalls}
          />
        )}

        {turn.kind === 'live' && turn.error}
      </Box>

      {turn.kind === 'persisted' && (
        <Box
          className="assistant-meta"
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            mt: 1,
            opacity: 0,
            transition: 'opacity 0.15s ease',
          }}
        >
          {narration && (
            <CopyToClipboard hideTextWhileCopying textToCopy={narration}>
              <ContentCopy
                sx={{ color: theme.brandV2.colors.treadGray3, fontSize: 14 }}
              />
            </CopyToClipboard>
          )}

          <Typography sx={{ color: theme.brandV2.colors.treadGray3, fontSize: '11px' }}>
            {formatMessageTimestamp(turn.message.createdAt)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

interface MessageListProps {
  // Rendered in place of the transcript when the active conversation has no content yet
  // (e.g. the panel's starter-prompt empty state).
  emptyState?: ReactNode;
  contentMaxWidthInPx?: number;
}

const MessageList = observer(({ contentMaxWidthInPx, emptyState }: MessageListProps) => {
  const theme = useTheme();
  const { treadAgentStore, userStore } = useStores();
  const { bulkConfirmToolCalls, cancelToolCall, confirmToolCall } = useTreadAgent();
  const companyId = getEffectiveUserCompanyId(userStore);
  const conversationId = treadAgentStore.activeConversationId;
  const [busyToolCallIds, setBusyToolCallIds] = useState<string[]>([]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const isTurnInProgress = treadAgentStore.isTurnInProgress;
  // The id of the message this turn just streamed live (null between turns). Its persisted row
  // skips the enter animation so it doesn't flash when the live turn hands off to history.
  const justStreamedMessageId = treadAgentStore.activeTurn.finalMessageId;

  // Only forces the re-render. The elapsed value comes from the store because this component
  // remounts when the panel switches layout, which would restart a mount-relative clock mid-turn.
  useEffect(() => {
    if (!isTurnInProgress) {
      return;
    }

    setNowMs(Date.now());
    const intervalId = setInterval(() => setNowMs(Date.now()), 100);

    return () => clearInterval(intervalId);
  }, [isTurnInProgress]);

  const elapsedMs = treadAgentStore.elapsedTurnMs(nowMs);

  // Auto-scrolls to the newest content as a turn streams, but yields the moment the user
  // scrolls up to read earlier messages.
  const { contentRef, isAtBottom, scrollRef, scrollToBottom } = useStickToBottom({
    initial: 'instant',
  });

  const {
    fetchOlder,
    hasOlder,
    isLoadingConversation,
    isLoadingOlder,
    liveTurn,
    messages,
  } = useConversationView(companyId, conversationId);

  // A chart already saved to the transcript (a refetch landed mid-turn) isn't drawn a second
  // time from the live turn.
  const savedVisualizationIds = new Set(
    messages.flatMap((message) => message.visualizations.map(({ id }) => id)),
  );
  const liveVisualizations = (liveTurn?.visualizations ?? []).filter(
    ({ id }) => !savedVisualizationIds.has(id),
  );

  // Scroll metrics captured the instant an older-history fetch is triggered, so the layout
  // effect can pin the viewport to the same message after older messages prepend. In-flight
  // de-duplication is handled by isLoadingOlder, not this ref — a failed fetch leaves the ref
  // set, but it never blocks a retry because the scroll guard doesn't read it.
  const pendingOlderScrollRef = useRef<{
    scrollHeight: number;
    scrollTop: number;
  } | null>(null);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      // Start fetching a little before the very top so older history is ready by the time the
      // user reaches the edge.
      const NEAR_TOP_PX = 120;

      if (hasOlder && !isLoadingOlder && el.scrollTop < NEAR_TOP_PX) {
        pendingOlderScrollRef.current = {
          scrollHeight: el.scrollHeight,
          scrollTop: el.scrollTop,
        };
        fetchOlder();
      }
    },
    [hasOlder, isLoadingOlder, fetchOlder],
  );

  useLayoutEffect(() => {
    const capture = pendingOlderScrollRef.current;
    const el = scrollRef.current;
    if (!capture || !el) {
      return;
    }

    // Older messages were just prepended; offset scrollTop by the added height so the message
    // that was at the top stays put instead of the view jumping.
    el.scrollTop = capture.scrollTop + (el.scrollHeight - capture.scrollHeight);
    pendingOlderScrollRef.current = null;
  }, [messages, scrollRef]);

  const resolveToolCall = useCallback(
    async (toolCallId: string, resolution: 'confirm' | 'cancel') => {
      if (!conversationId) {
        return;
      }

      setBusyToolCallIds((current) => [...current, toolCallId]);

      try {
        if (resolution === 'confirm') {
          await confirmToolCall(companyId, conversationId, toolCallId);
        } else {
          await cancelToolCall(companyId, conversationId, toolCallId);
        }
        // Leave the card busy until the invalidated query refetches the tool call in its
        // terminal state. Clearing here would re-enable the buttons for the frame between the
        // request resolving and the durable card catching up, allowing a double submit.
      } catch {
        // The request failed (the store already surfaced the error). Re-enable the still-pending
        // card so it stays actionable instead of stuck with both buttons disabled.
        setBusyToolCallIds((current) => current.filter((id) => id !== toolCallId));
      }
    },
    [companyId, conversationId, confirmToolCall, cancelToolCall],
  );

  const handleBulkConfirmToolCalls = useCallback(
    async (decision: BulkConfirmDecision) => {
      if (!conversationId) {
        return;
      }

      const submittedIds = [...decision.confirmedIds, ...decision.canceledIds];
      setBusyToolCallIds((current) => [...current, ...submittedIds]);

      try {
        await bulkConfirmToolCalls(companyId, conversationId, decision);
      } catch {
        // The store already surfaced the error. Re-enable the card so it stays actionable.
        setBusyToolCallIds((current) =>
          current.filter((id) => !submittedIds.includes(id)),
        );
      }
    },
    [companyId, conversationId, bulkConfirmToolCalls],
  );

  const handleConfirmToolCall = useCallback(
    (toolCallId: string) => resolveToolCall(toolCallId, 'confirm'),
    [resolveToolCall],
  );

  const handleCancelToolCall = useCallback(
    (toolCallId: string) => resolveToolCall(toolCallId, 'cancel'),
    [resolveToolCall],
  );

  const handleStartNewChat = useCallback(() => {
    treadAgentStore.closeConversation();
  }, [treadAgentStore]);

  const hasContent =
    messages.length > 0 ||
    (liveTurn !== null && (liveTurn.text !== '' || liveTurn.status !== 'idle'));
  const isLoaderShown = isLoadingConversation && !hasContent;

  useLayoutEffect(() => {
    void scrollToBottom('instant');
  }, [conversationId, isLoaderShown, scrollToBottom]);

  // Only block on the loader when there's nothing local to show yet (opening an existing
  // conversation from history). After a fresh chat's first send the just-sent message is folded
  // into the transcript cache and a streaming turn is open, so hasContent is true and we render
  // that rather than hiding it behind the loader while the conversation/messages settle.
  if (isLoaderShown) {
    // Fill the height (not just wrap the loader) so the composer stays anchored to the
    // bottom while a conversation loads.
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexGrow: 1,
          justifyContent: 'center',
          minHeight: 0,
        }}
      >
        <TreadAgentLoader size={32} />
      </Box>
    );
  }

  if (!hasContent && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
      {isLoadingOlder && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            left: 0,
            position: 'absolute',
            right: 0,
            top: 8,
            zIndex: 1,
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )}

      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          height: '100%',
          overflowY: 'auto',
          ...(contentMaxWidthInPx ? { scrollbarGutter: 'stable both-edges' } : {}),
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            boxSizing: 'border-box',
            maxWidth: contentMaxWidthInPx ? `${contentMaxWidthInPx}px` : undefined,
            mx: 'auto',
            p: 1.5,
            pb: 3,
            width: '100%',
          }}
        >
          {[
            ...messages.flatMap((message) => {
              if (message.role === LLMMessageRole.USER) {
                return [<UserMessageBubble key={message.id} message={message} />];
              }

              const turnTrace = treadAgentStore.traceForMessage(message.id);
              const hasMessageBody =
                Boolean(message.content) || message.toolCalls.length > 0 || turnTrace;

              return [
                ...message.visualizations.map((visualization) => (
                  <VisualizationBlock
                    key={`visualization-${visualization.id}`}
                    visualization={visualization}
                  />
                )),
                ...(hasMessageBody
                  ? [
                      <AssistantMessageBlock
                        key={treadAgentStore.turnKeyForMessage(message.id)}
                        busyToolCallIds={busyToolCallIds}
                        justStreamed={message.id === justStreamedMessageId}
                        onBulkConfirmToolCalls={handleBulkConfirmToolCalls}
                        onCancelToolCall={handleCancelToolCall}
                        onConfirmToolCall={handleConfirmToolCall}
                        turn={{
                          kind: 'persisted',
                          message,
                          turnTrace,
                        }}
                      />,
                    ]
                  : []),
              ];
            }),
            ...liveVisualizations.map((visualization) => (
              <VisualizationBlock
                key={`visualization-${visualization.id}`}
                visualization={visualization}
              />
            )),
            liveTurn ? (
              <AssistantMessageBlock
                key={treadAgentStore.turnKey}
                busyToolCallIds={busyToolCallIds}
                justStreamed
                onBulkConfirmToolCalls={handleBulkConfirmToolCalls}
                onCancelToolCall={handleCancelToolCall}
                onConfirmToolCall={handleConfirmToolCall}
                turn={{
                  kind: 'live',
                  error:
                    liveTurn.status === 'error' && liveTurn.errorMessage ? (
                      <ErrorPart
                        message={liveTurn.errorMessage}
                        onStartNewChat={handleStartNewChat}
                      />
                    ) : null,
                  streaming: liveTurn.status === 'streaming',
                  text: liveTurn.text,
                  trace:
                    liveTurn.status === 'streaming' ? (
                      <TurnTraceLive
                        elapsedLabel={formatElapsed(elapsedMs)}
                        trace={liveTurn.trace}
                      />
                    ) : (
                      <TurnTraceDisclosure
                        elapsedSeconds={elapsedMs / 1000}
                        trace={liveTurn.trace}
                      />
                    ),
                }}
              />
            ) : null,
          ]}
        </Box>
      </Box>

      {!isAtBottom && (
        <ButtonBase
          aria-label={t('tread_agent.scroll_to_latest')}
          onClick={() => scrollToBottom()}
          sx={{
            alignItems: 'center',
            backgroundColor: theme.brandV2.colors.treadGray9,
            border: `1px solid ${theme.brandV2.colors.treadGray7}`,
            borderRadius: '999px',
            bottom: 12,
            boxShadow: 1,
            display: 'flex',
            gap: 0.25,
            left: '50%',
            pl: 0.75,
            position: 'absolute',
            pr: 1,
            py: 0.5,
            transform: 'translateX(-50%)',
            '&:hover': { backgroundColor: theme.semantic.surfaceRaised },
          }}
        >
          <KeyboardArrowDown
            sx={{ color: theme.brandV2.colors.treadGray2, fontSize: 16 }}
          />
          <Typography
            sx={{
              color: theme.brandV2.colors.treadGray2,
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {t('tread_agent.latest')}
          </Typography>
        </ButtonBase>
      )}
    </Box>
  );
});

export default MessageList;
